import { FormEvent, useState } from 'react';

import { getErrorMessage } from '@/hooks/useApi';

import { AdminUser, useDeleteUser, useLookupUser } from './useUsers';

type SearchType = 'email' | 'userId';

export function UsersPage() {
  const [searchValue, setSearchValue] = useState('');
  const [searchType, setSearchType] = useState<SearchType>('email');
  const [error, setError] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);

  const lookupUser = useLookupUser();
  const deleteUser = useDeleteUser();

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = searchValue.trim();
    if (!trimmed) {
      setError('Please enter a search value');
      return;
    }

    try {
      await lookupUser.mutateAsync({ type: searchType, value: trimmed });
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser.mutateAsync(userToDelete.userId);
      setUserToDelete(null);
      lookupUser.reset();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const user = lookupUser.data;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Users</h1>
        <p className="mt-1 text-slate-600">Look up and manage user accounts</p>
      </div>

      {/* Search Form */}
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-slate-900">User Lookup</h2>

        {error && (
          <div role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="flex rounded-lg border border-slate-300">
            <button
              type="button"
              onClick={() => setSearchType('email')}
              className={`px-3 py-2 text-sm ${
                searchType === 'email'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              } rounded-l-lg`}
            >
              Email
            </button>
            <button
              type="button"
              onClick={() => setSearchType('userId')}
              className={`px-3 py-2 text-sm ${
                searchType === 'userId'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              } rounded-r-lg border-l border-slate-300`}
            >
              User ID
            </button>
          </div>

          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={searchType === 'email' ? 'user@example.com' : 'Enter user ID'}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2 focus:border-indigo-500 focus:outline-none"
          />

          <button
            type="submit"
            disabled={lookupUser.isPending}
            className="rounded-lg bg-indigo-600 px-6 py-2 text-white hover:bg-indigo-700 disabled:bg-indigo-400"
          >
            {lookupUser.isPending ? 'Searching...' : 'Search'}
          </button>
        </form>
      </div>

      {/* User Details Card */}
      {user && (
        <div className="mt-6 rounded-lg border border-slate-200 bg-white p-6">
          <div className="flex items-start justify-between">
            <h2 className="text-lg font-semibold text-slate-900">User Profile</h2>
            <button
              type="button"
              onClick={() => setUserToDelete(user)}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
            >
              Delete User
            </button>
          </div>

          <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="text-sm font-medium text-slate-500">User ID</dt>
              <dd className="mt-1 break-all font-mono text-sm text-slate-900">{user.userId}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-slate-500">Email</dt>
              <dd className="mt-1 text-sm text-slate-900">{user.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-slate-500">Name</dt>
              <dd className="mt-1 text-sm text-slate-900">{user.name || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-slate-500">Auth Provider</dt>
              <dd className="mt-1 text-sm text-slate-900">{user.authProvider}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-slate-500">Cognito Status</dt>
              <dd className="mt-1">
                <CognitoStatusBadge status={user.cognitoStatus} />
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-slate-500">Created</dt>
              <dd className="mt-1 text-sm text-slate-900">
                {new Date(user.createdAt).toLocaleDateString()}
              </dd>
            </div>
          </dl>

          {/* Token Balance */}
          <div className="mt-6 border-t border-slate-200 pt-4">
            <h3 className="text-sm font-semibold text-slate-900">Token Balance</h3>
            <div className="mt-2 grid gap-4 sm:grid-cols-3">
              <dl className="rounded-lg bg-slate-50 p-3">
                <dt className="text-xs font-medium text-slate-500">Total Balance</dt>
                <dd className="mt-1 text-xl font-bold text-slate-900">{user.tokens.balance}</dd>
              </dl>
              <dl className="rounded-lg bg-slate-50 p-3">
                <dt className="text-xs font-medium text-slate-500">Purchased</dt>
                <dd className="mt-1 text-xl font-bold text-slate-900">
                  {user.tokens.purchasedTokens}
                </dd>
              </dl>
              <dl className="rounded-lg bg-slate-50 p-3">
                <dt className="text-xs font-medium text-slate-500">Subscription</dt>
                <dd className="mt-1 text-xl font-bold text-slate-900">
                  {user.tokens.subscriptionTokens}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {userToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6">
            <h3 className="text-lg font-semibold text-slate-900">Delete User Account?</h3>
            <p className="mt-2 text-sm text-slate-600">
              This will permanently delete the user&apos;s Cognito account, DynamoDB profile, and
              cancel any active subscriptions. This action cannot be undone.
            </p>
            <div className="mt-3 rounded-lg bg-slate-50 p-3">
              <p className="text-sm font-medium text-slate-900">{userToDelete.email}</p>
              <p className="mt-1 font-mono text-xs text-slate-500">{userToDelete.userId}</p>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setUserToDelete(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteUser.isPending}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:bg-red-400"
              >
                {deleteUser.isPending ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CognitoStatusBadge({ status }: { status: string }) {
  const styles =
    status === 'CONFIRMED'
      ? 'bg-green-100 text-green-800'
      : status === 'FORCE_CHANGE_PASSWORD'
        ? 'bg-yellow-100 text-yellow-800'
        : 'bg-slate-100 text-slate-600';

  return <span className={`rounded-full px-2 py-1 text-xs font-medium ${styles}`}>{status}</span>;
}
